import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOmniAuth } from '@omni/auth'
import type { AdminNotice, EmailCopy, MySettings, Notice, Summary } from './words'

// Nateva's notifications, read and changed through the public.notify_* functions of nateva-platform
// (supabase/migrations/0085 to 0089). Each one answers for the person signed in, or the person an admin is
// seeing it as. The only things a person changes are their own: which notices are read, and their settings.
// The admin lists go through the same admin check as the People tab.

type Rpc = { rpc(fn: string, args?: Record<string, unknown>): PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }> }

function useDb(): Rpc {
  const { getClient } = useOmniAuth()
  return useMemo(() => getClient('crm').schema('public') as unknown as Rpc, [getClient])
}

const KEY = ['nateva', 'notify'] as const

async function call<T>(db: Rpc, fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await db.rpc(fn, args)
  if (error) throw new Error(error.message)
  return data as T
}

/** The number on the bell. Null when notifications are not switched on in this database (no bell then). */
export function useUnreadCount() {
  const db = useDb()
  return useQuery<number | null>({
    queryKey: [...KEY, 'unread'],
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await db.rpc('notify_unread_count')
      if (error) return null
      return typeof data === 'number' ? data : null
    },
  })
}

/** My notices, newest first. */
export function useNotices(opts: { limit?: number; app?: string | null; kind?: string | null; unreadOnly?: boolean; enabled?: boolean } = {}) {
  const db = useDb()
  const { limit = 30, app = null, kind = null, unreadOnly = false, enabled = true } = opts
  return useQuery<Notice[]>({
    queryKey: [...KEY, 'mine', limit, app, kind, unreadOnly],
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => (await call<Notice[] | null>(db, 'notify_mine', { p_limit: limit, p_app: app, p_kind: kind, p_unread_only: unreadOnly })) ?? [],
  })
}

/** Mark some notices read, or all of them (optionally one app's). While an admin is seeing it as someone, the database changes nothing. */
export function useMarkRead() {
  const db = useDb()
  const qc = useQueryClient()
  const done = () => { void qc.invalidateQueries({ queryKey: KEY }) }
  const some = useMutation({ mutationFn: (ids: string[]) => call<number>(db, 'notify_mark_read', { p_ids: ids }), onSettled: done })
  const all = useMutation({ mutationFn: (app: string | null) => call<number>(db, 'notify_mark_all_read', { p_app: app }), onSettled: done })
  return { some, all }
}

export function useMySettings() {
  const db = useDb()
  return useQuery<MySettings>({
    queryKey: [...KEY, 'settings'],
    queryFn: () => call<MySettings>(db, 'notify_my_settings'),
  })
}

/** Change my settings. A field left out stays as it is. */
export function useSaveSettings() {
  const db = useDb()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { email_copy?: boolean; muted?: string[] }) =>
      call<MySettings>(db, 'notify_set_settings', { p_email_copy: v.email_copy ?? null, p_muted: v.muted ?? null }),
    onSuccess: (data) => { qc.setQueryData([...KEY, 'settings'], data) },
    onSettled: () => { void qc.invalidateQueries({ queryKey: KEY }) },
  })
}

export function useAdminNotices(enabled: boolean, app: string | null, kind: string | null) {
  const db = useDb()
  return useQuery<AdminNotice[]>({
    queryKey: [...KEY, 'admin', 'notices', app, kind],
    enabled,
    queryFn: async () => (await call<AdminNotice[] | null>(db, 'notify_admin_notices', { p_limit: 200, p_app: app, p_kind: kind })) ?? [],
  })
}

export function useAdminCopies(enabled: boolean) {
  const db = useDb()
  return useQuery<EmailCopy[]>({
    queryKey: [...KEY, 'admin', 'copies'],
    enabled,
    queryFn: async () => (await call<EmailCopy[] | null>(db, 'notify_admin_email_copies', { p_limit: 200 })) ?? [],
  })
}

export function useAdminSummary(enabled: boolean) {
  const db = useDb()
  return useQuery<Summary>({
    queryKey: [...KEY, 'admin', 'summary'],
    enabled,
    queryFn: () => call<Summary>(db, 'notify_admin_summary'),
  })
}
